// Resilient fetch for every HTTP call the brain makes (REST, auth, endpoint
// list). Wraps a `fetch` rather than living in one client, so `RestClient`,
// `AuthClient` and the endpoint-list bootstrap all inherit it by construction
// and no future call site can forget it.
//
// Why this exists: a bare `fetch()` on a stalled mobile link does NOT reject
// promptly — Chromium holds a connected-but-dead socket open for minutes. On
// Android that outlived the native bridge's 30 s call timeout, so a network
// stall surfaced in the Compose UI as BRIDGE_TIMEOUT → "Not connected yet"
// (wrong cause, no retry, spinner forever). Bounding each attempt here fixes
// both: the abort lands as an AbortError, which `errorCode()` already maps to
// the NETWORK bridge code, so the UI names the real problem.
//
// What a filtered link looks like, and why one flat timeout could not tell it
// apart from a slow one (field report, Russian mobile carrier, no VPN): the
// filter enforces at flow SETUP, so an established WebSocket keeps carrying
// events while every new TLS handshake dies — silently, with no RST, and often
// only after ~15-20 KB have already flowed. Three failures therefore have to
// stay three different facts all the way into the log line:
//
//   timeout   we gave up waiting; the link may just be slow
//   rejected  the transport said no (DNS, refused, reset, TLS)
//   frozen    headers landed, then the body stopped arriving mid-flight
//
// Prime directive (Connection > Latency > Quality): abandoning at 9 s a request
// that would have answered at 12 s IS dropping something we could have
// degraded. So the first attempt stays snappy — it is cheap to lose and it
// buys the next one room — and the LAST attempt is the patient one, because
// nothing follows it. The cost of that shape is paid by the slow-but-working
// band: a link answering at 7 s used to succeed at 7 s and now gets cut, backs
// off and lands near 13 s. It still CONNECTS, which is what the prime directive
// ranks first; a flat budget wide enough for both ends would not fit the bridge.
//
// The one thing that may not stretch is the total. The Android bridge reaps a
// call at 30 s, and a bridge call is not always one request: an auth login is
// three sequential un-retryable POSTs plus an Argon2id derivation inside a
// SINGLE `account.restorePhrase`. So a request that will not be retried is
// budgeted as one LEG of that chain (HTTP_LONE_ATTEMPT_TIMEOUT_MS), not as if it
// owned the whole 30 s — otherwise the patient budget re-creates exactly the
// BRIDGE_TIMEOUT mis-blame this module exists to prevent.

/** First attempt: deliberately impatient. On a working link a TLS 1.3 handshake
 *  plus a small JSON body lands far inside this even on EDGE; when it does not,
 *  giving up early costs little and hands the wider budget below to a retry on
 *  a fresh connection. */
export const HTTP_FIRST_ATTEMPT_TIMEOUT_MS = 6_000;

/** Last attempt of a chain that HAS retries: patient. A full TLS handshake whose
 *  ClientHello is lost pays ~1 s + 3 s of TCP RTO backoff on top of ~3 RTT,
 *  which alone can outrun a 9 s budget on a congested 2G/EDGE cell. Nothing
 *  follows it inside this request, so it waits as long as the total affords. */
export const HTTP_LAST_ATTEMPT_TIMEOUT_MS = 14_000;

/** Growth factor between attempts, capped by HTTP_LAST_ATTEMPT_TIMEOUT_MS. */
export const HTTP_ATTEMPT_ESCALATION = 2;

/** How long a body may go SILENT after its headers before we call it frozen.
 *  An inactivity window, re-armed on every chunk — never a cap on how long a
 *  download may take. `GET /me/aliases` is a base64 blob of up to 87 400 chars
 *  (protocol/rest.ts) and needs seconds of steady transfer on a poor cell; a
 *  total window would fail it on every attempt and, worse, label a merely slow
 *  link as filtered. A connection still delivering bytes is a connection.
 *  The attempt deadline is what bounds the total. */
export const HTTP_BODY_STALL_MS = 4_000;

/**
 * Ceiling on acquiring an access token before a request can even be sent.
 *
 * `RestClient.request` awaits `getToken()` first, and that can fan out into a
 * serial recovery chain — refresh, then refresh again, then challenge+verify,
 * then a passkey login — each leg its own round trip. Unbounded, that chain
 * alone outlived the native bridge's 30 s budget on a slow link, so EVERY
 * native screen reported "not connected yet" at once without a single request
 * having been issued. This bounds the caller; the chain itself is left running,
 * because it may still succeed and leave a good session for the next attempt.
 */
export const TOKEN_ACQUISITION_BUDGET_MS = 8_000;

/** Extra attempts for replayable requests. */
export const HTTP_IDEMPOTENT_RETRIES = 1;

/** Base backoff between attempts; jittered to avoid a reconnect stampede. */
export const HTTP_RETRY_BASE_MS = 500;

/** Most of the chain that may be spent WAITING rather than trying. Doubling
 *  backoff outgrows any budget fast, and time spent sleeping buys no
 *  connection — Connection > Latency, so the attempts get the rest. */
export const HTTP_BACKOFF_SHARE = 0.25;

/**
 * The Android bridge (UiBridgeTransport.DEFAULT_CALL_TIMEOUT_MS) reaps a
 * registry call at 30 s and reports BRIDGE_TIMEOUT, which the UI words as
 * "not connected yet". Any HTTP chain that outlives this budget therefore gets
 * mis-blamed on the WebView instead of the network. Asserted by a test.
 */
export const HTTP_TOTAL_BUDGET_MS = 30_000;

/** Slack kept between the planned worst case and the bridge's hard reap, so a
 *  scheduling hiccup does not turn a network failure into a BRIDGE_TIMEOUT. */
export const HTTP_BUDGET_HEADROOM_MS = 1_000;

/**
 * Sequential un-retryable requests one bridge call may contain. The worst case
 * is a password restore: `account.firstLogin` derives the auth key
 * (`/auth/challenge`), then logs in (`/auth/challenge`, `/auth/verify`) — three
 * lone POSTs inside one `account.restorePhrase`, which the Android bridge reaps
 * at HTTP_TOTAL_BUDGET_MS with no INTERACTIVE override (MerkuryRepository:535).
 */
export const HTTP_MAX_CHAIN_LEGS = 3;

/** Non-HTTP time the same bridge call spends between those legs: one Argon2id
 *  derivation (64 MB, t=3 — docs/03 §2) on a low-end phone, plus parsing. */
export const HTTP_CHAIN_RESERVE_MS = 4_000;

/**
 * Widest budget for a request that will NOT be retried. A lone request is
 * patient — nothing follows it inside the request — but it is usually one leg
 * of the login chain above, so it gets the per-leg share of the bridge budget
 * rather than the whole of it. Derived, not chosen: the chain arithmetic and
 * the constant cannot drift apart (asserted by `worstCaseChainBudgetMs`).
 */
export const HTTP_LONE_ATTEMPT_TIMEOUT_MS = Math.floor(
  (HTTP_TOTAL_BUDGET_MS - HTTP_BUDGET_HEADROOM_MS - HTTP_CHAIN_RESERVE_MS) / HTTP_MAX_CHAIN_LEGS,
);

/**
 * Why an HTTP attempt died. `errorCode()` still collapses all of these to the
 * NETWORK bridge code (the UI has one sentence for "the network is not working
 * right now"), but `errKind()` keeps them apart in the log, which is the only
 * place the difference between "slow" and "blocked" can be read.
 */
export type HttpFailureKind = 'timeout' | 'rejected' | 'frozen' | 'cancelled';

/**
 * The tag is a side table, not a property. A property named `httpKind` would be
 * forgeable: any thrown value carrying one — a deserialized wire object, a
 * server-chosen string that ended up on an error — would be read as OUR
 * classification and collapse to NETWORK. Only a value this module tagged
 * itself is in the map, and a WeakMap also survives a frozen error object,
 * which `defineProperty` did not, and never serializes into a body or a log.
 */
const KINDS = new WeakMap<object, HttpFailureKind>();

/** The kind of an HTTP failure, or undefined for anything not from here. */
export function httpFailureKind(err: unknown): HttpFailureKind | undefined {
  if (err === null || (typeof err !== 'object' && typeof err !== 'function')) return undefined;
  return KINDS.get(err as object);
}

function tagKind<T>(err: T, kind: HttpFailureKind): T {
  if (err !== null && (typeof err === 'object' || typeof err === 'function')) {
    KINDS.set(err as object, kind);
  }
  return err;
}

export interface HttpFailureInfo {
  kind: HttpFailureKind;
  /** 1-based. */
  attempt: number;
  attempts: number;
  /** The window that expired: the attempt deadline, or the body stall window. */
  budgetMs: number;
  /** Public endpoint hostname, or `[ip]`/`[host]` when the endpoint has no name
   *  we may print. Never the path (user ids) and never a literal address. */
  host: string;
  willRetry: boolean;
}

export interface DeadlineScheduleOptions {
  /** First attempt's budget. */
  firstMs?: number;
  /** Widest budget any attempt may get; the last attempt is given exactly this
   *  when the total allows. */
  lastMs?: number;
  escalation?: number;
  retryBaseMs?: number;
  /** Hard ceiling the whole chain must fit inside. */
  totalBudgetMs?: number;
  /** Non-HTTP time a multi-leg bridge call also spends (the login KDF). */
  chainReserveMs?: number;
  /** Spent before the first byte and therefore unavailable to the schedule. */
  reserveMs?: number;
  headroomMs?: number;
}

/** Budget left for the request chain once the token chain and the slack are
 *  taken out — before any of it goes to backoff. */
function spendableMs(o: DeadlineScheduleOptions): number {
  const total = o.totalBudgetMs ?? HTTP_TOTAL_BUDGET_MS;
  const reserve = o.reserveMs ?? TOKEN_ACQUISITION_BUDGET_MS;
  const headroom = o.headroomMs ?? HTTP_BUDGET_HEADROOM_MS;
  return Math.max(1, total - reserve - headroom);
}

/**
 * Backoff before each retry, worst case (full jitter spends at most the whole
 * window). Doubling is only affordable while it stays a minority of the chain:
 * past that the plan is squeezed, because sleeping is the one part of the chain
 * that can never produce a connection.
 */
export function retryBackoffPlanMs(attempts: number, o: DeadlineScheduleOptions = {}): number[] {
  const base = o.retryBaseMs ?? HTTP_RETRY_BASE_MS;
  const ideal: number[] = [];
  for (let i = 0; i < attempts - 1; i += 1) ideal.push(base * 2 ** i);
  const total = ideal.reduce((a, b) => a + b, 0);
  const cap = Math.floor(spendableMs(o) * HTTP_BACKOFF_SHARE);
  if (total <= cap) return ideal;
  const scale = cap / total;
  return ideal.map((ms) => Math.max(1, Math.floor(ms * scale)));
}

export function retryBackoffTotalMs(attempts: number, o: DeadlineScheduleOptions = {}): number {
  return retryBackoffPlanMs(attempts, o).reduce((a, b) => a + b, 0);
}

function chainBudgetMs(attempts: number, o: DeadlineScheduleOptions): number {
  return Math.max(1, spendableMs(o) - retryBackoffTotalMs(attempts, o));
}

/**
 * Per-attempt deadlines, escalating, and guaranteed to fit the chain budget.
 *
 * The last attempt is pinned to the widest budget because nothing follows it —
 * which is also why a request that may not be replayed gets the patient budget
 * on its single attempt instead of the impatient one. Asking for more retries
 * buys shorter attempts rather than a longer chain: the total is what the
 * Android bridge reaps on, so it is the one thing that may not stretch.
 *
 * A LONE attempt is patient but not unbounded: it may be one of
 * HTTP_MAX_CHAIN_LEGS sequential requests inside one bridge call, so its ceiling
 * is the per-leg share. A chain that retries is a single leg (the token
 * acquisition it follows is already reserved), so it keeps the full width.
 */
export function attemptDeadlinesMs(attempts: number, o: DeadlineScheduleOptions = {}): number[] {
  if (attempts <= 0) return [];
  const firstMs = o.firstMs ?? HTTP_FIRST_ATTEMPT_TIMEOUT_MS;
  const widest = attempts === 1 ? HTTP_LONE_ATTEMPT_TIMEOUT_MS : HTTP_LAST_ATTEMPT_TIMEOUT_MS;
  const lastMs = o.lastMs ?? Math.max(firstMs, widest);
  const escalation = o.escalation ?? HTTP_ATTEMPT_ESCALATION;

  const ideal: number[] = [];
  for (let i = 0; i < attempts; i += 1) {
    ideal.push(Math.min(lastMs, Math.round(firstMs * escalation ** i)));
  }
  ideal[attempts - 1] = lastMs;

  const chain = chainBudgetMs(attempts, o);
  const total = ideal.reduce((a, b) => a + b, 0);
  if (total <= chain) return ideal;
  const scale = chain / total;
  return ideal.map((ms) => Math.max(1, Math.floor(ms * scale)));
}

/**
 * Worst-case wall time a bridge call can spend in HTTP: acquiring a token, then
 * the request itself with its retries and full jitter. Computed from the same
 * schedule the request actually runs, so it cannot drift away from it.
 */
export function worstCaseBudgetMs(
  retries = HTTP_IDEMPOTENT_RETRIES,
  o: DeadlineScheduleOptions = {},
): number {
  const attempts = retries + 1;
  const reserve = o.reserveMs ?? TOKEN_ACQUISITION_BUDGET_MS;
  const backoff = retryBackoffTotalMs(attempts, o);
  const deadlines = attemptDeadlinesMs(attempts, o);
  return deadlines.reduce((a, b) => a + b, reserve + backoff);
}

/**
 * Worst-case wall time a bridge call spends when it issues `legs` sequential
 * un-retryable requests plus the work between them (`account.firstLogin`). This
 * — not `worstCaseBudgetMs` — is the number the Android bridge's 30 s reap has
 * to clear for a login, because `worstCaseBudgetMs` only ever modelled ONE
 * request and a login is three.
 */
export function worstCaseChainBudgetMs(
  legs = HTTP_MAX_CHAIN_LEGS,
  o: DeadlineScheduleOptions = {},
): number {
  const perLeg = attemptDeadlinesMs(1, o).reduce((a, b) => a + b, 0);
  return legs * perLeg + (o.chainReserveMs ?? HTTP_CHAIN_RESERVE_MS);
}

/** Reject with an AbortError if `work` outruns `budgetMs`. The work keeps
 *  running — it may still land a usable result for the next caller. */
export async function withDeadline<T>(
  work: Promise<T>,
  budgetMs: number,
  label: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(tagKind(abortError(`${label} exceeded ${budgetMs}ms`), 'timeout')),
      budgetMs,
    );
  });
  work.catch(() => undefined); // the loser must not surface as unhandled
  try {
    return await Promise.race([work, deadline]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** Methods safe to replay: no server-side effect, so a retry cannot double-apply. */
const IDEMPOTENT = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Replay-proof regardless of what the caller asks for. `POST /auth/refresh`
 * rotates the refresh token: a replay that reaches the server twice burns the
 * rotation and logs the device out (docs/03 §3), and a client cannot tell a
 * lost response from a lost request. Opt-in is a caller's judgement everywhere
 * except here.
 */
const NEVER_REPLAY = /\/auth\/refresh$/;

export interface ResilientInit extends RequestInit {
  /**
   * Override the method rule for THIS request. `true` replays it on a transport
   * failure — only for writes whose server effect is idempotent or absent;
   * `false` forbids replay even for a GET. Default: GET/HEAD/OPTIONS only.
   */
  retry?: boolean;
}

export type ResilientFetch = (
  input: RequestInfo | URL,
  init?: ResilientInit,
) => Promise<Response>;

/**
 * The production `fetch` seam: bounded, retrying, and logging its failure kind.
 * Clients take this as their default so no call site can ship an unbounded
 * request; tests keep injecting their own `fetchFn` and are unaffected.
 *
 * Wraps rather than references the global: a bare `fetch` invoked as a method
 * loses its window binding and throws "Illegal invocation" in Chromium.
 */
export function defaultFetch(options?: ResilientFetchOptions): ResilientFetch {
  return resilientFetch((...args) => fetch(...args), { onFailure: logHttpFailure, ...options });
}

export interface ResilientFetchOptions extends DeadlineScheduleOptions {
  /** Fixed per-attempt budget: pins both ends of the escalation, so every
   *  attempt gets exactly this. Prefer `firstMs`/`lastMs`. */
  timeoutMs?: number;
  /** Extra attempts for replayable requests (0 disables retrying). */
  retries?: number;
  /** How long a body may stall after its headers before it counts as frozen. */
  bodyStallMs?: number;
  /** Injectable for tests. */
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
  /** Injectable jitter source in [0,1). */
  random?: () => number;
  /** Notified for every failed attempt, retried or not. */
  onFailure?: (info: HttpFailureInfo) => void;
}

/**
 * The default failure log. Host and kind only: a path carries user ids and a
 * query can carry anything, so neither may reach a log (docs/03 §5). Public
 * endpoint hostnames are not secrets — which is the whole point, since "which
 * of our three domains dies, and how" is the diagnosis — but an IP endpoint has
 * no such name, and `hostOf` has already replaced it before we get here. This
 * mirrors into `runtime/logRing`, which `dev.logs` exports, so the redaction has
 * to happen at the source rather than at the sink.
 */
export function logHttpFailure(info: HttpFailureInfo): void {
  if (info.kind === 'cancelled') return; // a caller changing its mind is not a fault
  const console_ = (globalThis as { console?: { warn?: (...args: unknown[]) => void } }).console;
  console_?.warn?.(
    `http ${info.kind}: host=${info.host || 'relative'} attempt=${info.attempt}/${info.attempts} budget=${info.budgetMs}ms${info.willRetry ? ' retrying' : ''}`,
  );
}

/** True when a fresh connection is worth trying. A caller's own abort is not. */
function isRetriable(kind: HttpFailureKind | undefined): boolean {
  return kind === 'timeout' || kind === 'rejected' || kind === 'frozen';
}

/** Classify a rejection we did not raise ourselves. */
function classify(err: unknown): HttpFailureKind | undefined {
  if (err instanceof TypeError) return 'rejected';
  if (err instanceof Error && err.name === 'AbortError') return 'timeout';
  return undefined;
}

/** Statuses the Response constructor forbids a body on (RFC 9110 null-body). */
const NULL_BODY = new Set([101, 103, 204, 205, 304]);

/**
 * The iOS brain runs in a bare JSContext with no `AbortController`,
 * `DOMException`, or `Response` (runtime/native/http.ts provides only a
 * minimal fetch). Feature-detect instead of referencing them: the deadlines
 * below are `setTimeout` races, so "nothing hangs unbounded" holds without
 * an abort primitive — we just can't cancel the losing attempt's socket
 * (the native layer owns transport timeouts there).
 */
function abortError(message: string): Error {
  if (typeof DOMException !== 'undefined') return new DOMException(message, 'AbortError');
  const err = new Error(message);
  err.name = 'AbortError';
  return err;
}

function newAbortController(): AbortController | undefined {
  return typeof AbortController !== 'undefined' ? new AbortController() : undefined;
}

function rawUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  const href = (input as { href?: unknown }).href;
  if (typeof href === 'string') return href;
  const url = (input as { url?: unknown }).url;
  return typeof url === 'string' ? url : '';
}

/**
 * A host safe to name in a log: dot-separated labels ending in an alphabetic
 * TLD. Deliberately an ALLOW-list. The endpoint list carries `kind: 'ip'`
 * entries (protocol/endpoints.ts) and REST follows the winning signaling origin,
 * so `hostOf` genuinely sees raw addresses — and "no tokens, IPs or UUIDs in
 * logs" is non-negotiable (CLAUDE.md). Every spelling of an address fails this
 * test: a dotted quad ends in digits, IPv6 carries `:`/brackets, and the
 * decimal/hex/octal forms the URL parser accepts are normalized to one of those
 * (or have no dot at all). Anything that fails is replaced, never abbreviated,
 * so no octet can reach a log line by construction.
 */
const LABEL = '[a-z0-9](?:[a-z0-9-]*[a-z0-9])?';
// The TLD must start with letters, which is what excludes every numeric host.
// `xn--` is spelled out because an IDN mirror (a .рф domain is xn--p1ai) has
// digits in its TLD and must still be nameable — this list exists so a censored
// user's log says WHICH mirror died.
const DNS_NAME = new RegExp(`^${LABEL}(?:\\.${LABEL})*\\.(?:[a-z]{2,}|xn--[a-z0-9-]{2,})$`, 'i');

/** Stand-ins for a host we may not print. The distinction between them is the
 *  useful half of the diagnosis ("we are down to the IP fallback tier") and
 *  leaks nothing: it names a tier, not an address. */
const IP_HOST = '[ip]';
const OPAQUE_HOST = '[host]';

function looksLikeAddress(host: string): boolean {
  return host.includes(':') || host.startsWith('[') || /^[0-9.]+$/.test(host);
}

/** Hostname only — never the path or query, which carry ids — and never a
 *  literal address, which is an id of its own. */
function hostOf(input: RequestInfo | URL): string {
  const raw = rawUrl(input);
  let host: string;
  if (typeof URL !== 'undefined') {
    try {
      host = new URL(raw).hostname;
    } catch {
      return ''; // relative URL: no host to name
    }
  } else {
    const match = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)/i.exec(raw);
    const authority = match?.[1] ?? '';
    host = authority.replace(/^[^@]*@/, '').replace(/:\d+$/, '');
  }
  if (host === '') return '';
  if (DNS_NAME.test(host)) return host;
  return looksLikeAddress(host) ? IP_HOST : OPAQUE_HOST;
}

function replayForbidden(input: RequestInfo | URL): boolean {
  const path = (rawUrl(input).split('#')[0] ?? '').split('?')[0] ?? '';
  return NEVER_REPLAY.test(path);
}

function rebuild(response: Response, bytes: ArrayBuffer | Uint8Array): Response {
  return new Response(bytes as BodyInit, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}

/**
 * Read the body to completion and rebuild an equivalent Response, so the
 * caller's later `.json()` cannot block. Status, statusText and headers are
 * preserved because `RestClient` branches on all three.
 *
 * Drained chunk by chunk rather than through `arrayBuffer()` so `onChunk` can
 * re-arm the stall window: the freeze we are hunting is silence, not slowness,
 * and an 85 KB alias blob on a bad cell is slow for many seconds while making
 * perfectly good progress.
 */
async function buffered(response: Response, onChunk: () => void): Promise<Response> {
  if (NULL_BODY.has(response.status) || !response.body) return response;
  if (typeof Response === 'undefined') return response; // brain: NativeResponse is pre-buffered
  const stream = response.body as { getReader?: () => ReadableStreamDefaultReader<Uint8Array> };
  // No stream reader (a polyfilled fetch): fall back to a single read. The
  // attempt deadline still bounds it; only the freeze/slow distinction is lost.
  if (typeof stream.getReader !== 'function') return rebuild(response, await response.arrayBuffer());

  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value === undefined) continue;
    chunks.push(value);
    size += value.byteLength;
    onChunk();
  }
  const bytes = new Uint8Array(size);
  let at = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, at);
    at += chunk.byteLength;
  }
  return rebuild(response, bytes);
}

/** Sleep that never leaves a dangling timer when the caller aborts. */
function delay(
  ms: number,
  signal: AbortSignal | undefined,
  setTimeoutFn: typeof setTimeout,
  clearTimeoutFn: typeof clearTimeout,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeoutFn(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort(): void {
      clearTimeoutFn(timer);
      reject(tagKind(abortError('aborted'), 'cancelled'));
    }
    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Decorate a `fetch` with escalating per-attempt deadlines and bounded retry.
 * The returned function has fetch's signature plus the per-request `retry`
 * opt-in, so it drops into any `fetchFn` seam.
 *
 * A caller-supplied `init.signal` still works and always wins: aborting it
 * aborts the in-flight attempt and stops any pending retry.
 */
export function resilientFetch(
  base: typeof fetch,
  options: ResilientFetchOptions = {},
): ResilientFetch {
  const schedule: DeadlineScheduleOptions = { ...options };
  const firstMs = options.firstMs ?? options.timeoutMs;
  const lastMs = options.lastMs ?? options.timeoutMs;
  if (firstMs !== undefined) schedule.firstMs = firstMs;
  if (lastMs !== undefined) schedule.lastMs = lastMs;
  const maxRetries = options.retries ?? HTTP_IDEMPOTENT_RETRIES;
  const bodyStallMs = options.bodyStallMs ?? HTTP_BODY_STALL_MS;
  const setTimeoutFn = options.setTimeoutFn ?? setTimeout;
  const clearTimeoutFn = options.clearTimeoutFn ?? clearTimeout;
  const random = options.random ?? Math.random;
  const onFailure = options.onFailure;

  return async (input: RequestInfo | URL, init?: ResilientInit): Promise<Response> => {
    const method = (init?.method ?? 'GET').toUpperCase();
    const caller = init?.signal ?? undefined;
    const replayable = (init?.retry ?? IDEMPOTENT.has(method)) && !replayForbidden(input);
    const attempts = replayable ? maxRetries + 1 : 1;
    const deadlines = attemptDeadlinesMs(attempts, schedule);
    const backoffs = retryBackoffPlanMs(attempts, schedule);
    const forwarded: ResilientInit = { ...init };
    delete forwarded.retry; // ours, not the wire's

    let lastError: unknown;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      if (caller?.aborted) throw tagKind(abortError('aborted'), 'cancelled');

      const budgetMs = deadlines[attempt] ?? HTTP_LONE_ATTEMPT_TIMEOUT_MS;
      const controller = newAbortController();
      let failed: { kind: HttpFailureKind; message: string; budgetMs: number } | undefined;
      let fire: ((kind: HttpFailureKind, message: string, windowMs: number) => void) | undefined;
      // Set once the headers have landed; cleared again by the first body chunk.
      // A deadline that expires in that window is a body that never started —
      // the documented mid-flight freeze — and not merely a slow request.
      let silentBody = false;
      // Race rather than trust: aborting the signal is what frees the socket,
      // but the deadline must hold even if the underlying fetch ignores its
      // signal — the whole point here is that nothing can hang unbounded.
      const deadline = new Promise<never>((_, reject) => {
        fire = (kind, message, windowMs) => {
          failed = { kind, message, budgetMs: windowMs };
          controller?.abort();
          reject(tagKind(abortError(message), kind));
        };
      });
      const timer = setTimeoutFn(
        () =>
          silentBody
            ? fire?.('frozen', `response body stalled after ${budgetMs}ms`, budgetMs)
            : fire?.('timeout', `request timed out after ${budgetMs}ms`, budgetMs),
        budgetMs,
      );
      let bodyTimer: ReturnType<typeof setTimeout> | undefined;
      const onCallerAbort = (): void => controller?.abort();
      caller?.addEventListener('abort', onCallerAbort, { once: true });

      try {
        const call = base(input, {
          ...forwarded,
          ...(controller ? { signal: controller.signal } : {}),
        });
        // The loser of the race must not surface as an unhandled rejection.
        call.catch(() => undefined);
        const response = await Promise.race([call, deadline]);
        // `fetch` resolves as soon as the HEADERS land, so a socket that stalls
        // mid-body would escape the headers deadline entirely. The attempt timer
        // above keeps running — it is what `worstCaseBudgetMs` counts, and it
        // must bound the whole attempt, headers and body alike. On top of it the
        // body gets a SILENCE window, re-armed on every chunk: that is the one
        // distinction that matters under mid-flight filtering, and unlike a
        // total window it never punishes a big body on a slow link (an 85 KB
        // alias blob takes seconds of honest transfer). It can therefore only
        // ever cut EARLIER than the attempt deadline, never later.
        const armStall = (progressed: boolean): void => {
          if (progressed) silentBody = false;
          if (bodyTimer !== undefined) clearTimeoutFn(bodyTimer);
          bodyTimer = setTimeoutFn(
            () => fire?.('frozen', `response body stalled after ${bodyStallMs}ms`, bodyStallMs),
            bodyStallMs,
          );
        };
        silentBody = true;
        armStall(false);
        const drain = buffered(response, () => armStall(true));
        drain.catch(() => undefined); // the loser of the race, again
        return await Promise.race([drain, deadline]);
      } catch (err) {
        const cancelled = caller?.aborted === true;
        const kind = cancelled ? 'cancelled' : (failed?.kind ?? classify(err));
        // Our own timer's rejection can lose the race to the abort it triggers,
        // so rebuild it rather than report whichever error arrived first.
        const raw = failed && !cancelled ? abortError(failed.message) : err;
        lastError = kind ? tagKind(raw, kind) : raw;
        const willRetry = !cancelled && attempt < attempts - 1 && isRetriable(kind);
        if (kind && onFailure) {
          onFailure({
            kind,
            attempt: attempt + 1,
            attempts,
            budgetMs: failed?.budgetMs ?? budgetMs,
            host: hostOf(input),
            willRetry,
          });
        }
        // The caller pulled the plug — not our failure to retry.
        if (!willRetry) throw lastError;
      } finally {
        clearTimeoutFn(timer);
        if (bodyTimer !== undefined) clearTimeoutFn(bodyTimer);
        caller?.removeEventListener('abort', onCallerAbort);
      }

      // Full jitter: spread retries so a whole app waking up on one cell
      // doesn't hit the origin in lockstep.
      const backoff = backoffs[attempt] ?? 0;
      await delay(Math.floor(backoff * random()) + 1, caller, setTimeoutFn, clearTimeoutFn);
    }
    throw lastError;
  };
}
